<template>
  <div class="provider-dashboard-container">
    <!-- Unauthorized Access -->
    <div v-if="!isAuthenticated" class="error-container">
      <n-card class="max-w-md text-center">
        <n-icon size="64" color="#ef4444" class="mb-4">
          <ShieldX />
        </n-icon>
        <n-h2>Access Denied</n-h2>
        <n-text depth="3">You must be logged in as an admin to access this page.</n-text>
      </n-card>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="loading-container">
      <n-spin size="large" />
      <n-text depth="3" class="mt-4 block">Loading admin dashboard...</n-text>
    </div>

    <!-- Main Dashboard -->
    <div v-else>
      <!-- Header -->
      <n-card class="mb-6" :bordered="false">
        <div class="flex justify-between items-center">
          <div>
            <n-h1 class="mb-1">Admin Dashboard</n-h1>
            <n-text depth="3">Manage provider verifications</n-text>
          </div>
          <n-tag type="info" :bordered="false" size="large">
            <template #icon>
              <n-icon><Shield /></n-icon>
            </template>
            Admin
          </n-tag>
        </div>
      </n-card>

      <!-- Stats Overview -->
      <n-grid cols="1 s:2 m:3" responsive="screen" :x-gap="16" :y-gap="16" class="mb-6">
        <n-grid-item>
          <n-card>
            <div class="flex items-center">
              <n-icon size="40" color="#3b82f6">
                <Users />
              </n-icon>
              <div class="ml-4">
                <n-text depth="3" class="text-sm">Total Providers</n-text>
                <n-h3 class="mb-0">{{ stats.totalProviders }}</n-h3>
              </div>
            </div>
          </n-card>
        </n-grid-item>

        <n-grid-item>
          <n-card>
            <div class="flex items-center">
              <n-icon size="40" color="#f59e0b">
                <Clock />
              </n-icon>
              <div class="ml-4">
                <n-text depth="3" class="text-sm">Pending Verifications</n-text>
                <n-h3 class="mb-0">{{ stats.pendingVerifications }}</n-h3>
              </div>
            </div>
          </n-card>
        </n-grid-item>

        <n-grid-item>
          <n-card>
            <div class="flex items-center">
              <n-icon size="40" color="#10b981">
                <CheckCircle />
              </n-icon>
              <div class="ml-4">
                <n-text depth="3" class="text-sm">Verified Providers</n-text>
                <n-h3 class="mb-0">{{ stats.verifiedProviders }}</n-h3>
              </div>
            </div>
          </n-card>
        </n-grid-item>
      </n-grid>

      <!-- Main Content -->
      <n-card>
        <div class="space-y-6">
          <div class="flex justify-between items-center">
            <n-h2 class="mb-0">Provider Verifications</n-h2>
            
            <!-- Filters -->
            <div class="flex items-center space-x-4">
              <n-input 
                v-model:value="searchTerm" 
                placeholder="Search providers..."
                clearable
                style="width: 250px"
              >
                <template #prefix>
                  <n-icon><Search /></n-icon>
                </template>
              </n-input>
              
              <n-select
                v-model:value="filterStatus"
                :options="statusOptions"
                placeholder="Filter by status"
                style="width: 200px"
                @update:value="handleFilterChange"
              />
              
              <n-button 
                @click="showPendingOnly = !showPendingOnly"
                :type="showPendingOnly ? 'primary' : 'default'"
              >
                <template #icon>
                  <n-icon><FileCheck /></n-icon>
                </template>
                {{ showPendingOnly ? 'All' : 'Pending Only' }}
              </n-button>
            </div>
          </div>

          <!-- Providers Table -->
          <n-data-table
            :columns="providerColumns"
            :data="filteredProviders"
            :pagination="paginationConfig"
            :bordered="false"
            :loading="loadingProviders"
            @update:page="handlePageChange"
          />
        </div>
      </n-card>
    </div>

    <!-- Provider Verification Modal - Minimal -->
    <n-modal 
      v-model:show="showVerificationModal" 
      preset="card" 
      style="width: 450px"
    >
      <template #header>
        <div class="flex items-center justify-between">
          <span>{{ selectedProvider?.name }}</span>
        </div>
      </template>

      <div v-if="selectedProvider" class="space-y-4">
        <!-- Documents Link -->
        <div v-if="selectedProvider.verification_info?.clockclear_url" class="text-center py-4">
          <n-button 
            tag="a" 
            :href="selectedProvider.verification_info.clockclear_url" 
            target="_blank"
            type="primary"
            size="large"
            block
          >
            <template #icon>
              <n-icon><ExternalLink /></n-icon>
            </template>
            View Documents on ClockClear
          </n-button>
          <p class="text-xs text-gray-500 mt-2">
            Documents are encrypted and deleted after viewing
          </p>
        </div>

        <!-- Compact Metadata -->
        <div class="text-sm text-gray-600 space-y-1">
          <div class="flex justify-between">
            <span>Submitted:</span>
            <span>{{ selectedProvider.verification_info?.submitted_at ? formatDateTime(selectedProvider.verification_info.submitted_at) : 'N/A' }}</span>
          </div>
          <div class="flex justify-between">
            <span>Signature:</span>
            <span>{{ selectedProvider.verification_info?.digital_signature_confirmed ? 'Confirmed' : 'Not confirmed' }}</span>
          </div>
        </div>

        <!-- Admin Notes (Optional) -->
        <n-input
          v-model:value="adminNotes"
          type="textarea"
          placeholder="Add notes (optional)"
          :rows="2"
        />
      </div>

      <n-alert v-else type="warning" size="small">
        No verification data available
      </n-alert>

      <template #footer>
        <!-- Actions moved to footer -->
        <div class="flex gap-2">
          <n-button @click="showVerificationModal = false">
            Cancel
          </n-button>
          <n-button 
            type="error"
            @click="handleUpdateStatus('rejected')"
            :loading="updatingStatus"
          >
            <template #icon>
              <n-icon><XCircle /></n-icon>
            </template>
            Reject
          </n-button>
          <n-button 
            type="success"
            @click="handleUpdateStatus('verified')"
            :loading="updatingStatus"
          >
            <template #icon>
              <n-icon><CheckCircle /></n-icon>
            </template>
            Approve
          </n-button>
        </div>
      </template>
    </n-modal>

    <!-- Translate Confirmation Modal -->
    <n-modal 
      v-model:show="showTranslateModal" 
      preset="card" 
      style="width: 400px"
    >
      <template #header>
        <div class="flex items-center gap-2">
          <n-icon color="#6366f1"><Languages /></n-icon>
          <span>Confirm Translation</span>
        </div>
      </template>

      <div class="space-y-4">
        <n-alert type="warning" :bordered="false">
          <template #header>This will trigger a Groq API call</template>
          Translating provider: <strong>{{ translateProvider?.name }}</strong>
        </n-alert>

        <div>
          <n-text depth="3" class="block mb-2">
            Type <strong>TRANSLATE</strong> to confirm:
          </n-text>
          <n-input 
            v-model:value="translateConfirmText" 
            placeholder="Type TRANSLATE"
            :status="translateConfirmText && translateConfirmText !== 'TRANSLATE' ? 'error' : undefined"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex gap-2 justify-end">
          <n-button @click="closeTranslateModal">
            Cancel
          </n-button>
          <n-button 
            type="primary"
            :disabled="translateConfirmText !== 'TRANSLATE'"
            :loading="translating"
            @click="confirmTranslate"
          >
            <template #icon>
              <n-icon><Languages /></n-icon>
            </template>
            Translate
          </n-button>
        </div>
      </template>
    </n-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import { 
  NCard, NButton, NIcon, NH1, NH2, NH3, NH4, NText, NGrid, NGridItem, 
  NInput, NSelect, NDataTable, NModal, NDescriptions, NDescriptionsItem,
  NTag, NSpin, NAlert, useMessage 
} from 'naive-ui'
import {
  Users, Clock, CheckCircle, XCircle, Search, Shield, FileCheck, 
  ShieldX, Eye, ExternalLink, Languages
} from 'lucide-vue-next'
import { getAdminProviders, updateProviderStatus, adminTranslateProvider } from '../services/admin'
import { authToken, isAuthenticated } from '../services/auth'

// State
const loading = ref(true)
const loadingProviders = ref(false)
const updatingStatus = ref(false)
const searchTerm = ref('')
const filterStatus = ref('all')
const showPendingOnly = ref(false)
const showVerificationModal = ref(false)
const selectedProvider = ref(null)
const adminNotes = ref('')

// Translate modal state
const showTranslateModal = ref(false)
const translateProvider = ref(null)
const translateConfirmText = ref('')
const translating = ref(false)

const providers = ref([])
const totalProviders = ref(0)
const currentPage = ref(1)
const totalPages = ref(0)
const perPage = ref(20)

const stats = ref({
  totalProviders: 0,
  pendingVerifications: 0,
  verifiedProviders: 0
})

const message = useMessage()

// Computed
const statusOptions = [
  { label: 'All Status', value: 'all' },
  { label: 'Verified', value: 'verified' },
  { label: 'Unverified', value: 'unverified' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Suspended', value: 'suspended' }
]

const filteredProviders = computed(() => {
  return providers.value.filter(provider => {
    const matchesSearch = !searchTerm.value || 
      provider.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    
    return matchesSearch
  })
})

const paginationConfig = computed(() => ({
  page: currentPage.value,
  pageSize: perPage.value,
  pageCount: totalPages.value,
  showSizePicker: true,
  pageSizes: [10, 20, 50, 100],
  onUpdatePageSize: (pageSize) => {
    perPage.value = pageSize
    loadProviders()
  }
}))

// Table columns
const providerColumns = [
  {
    title: 'Provider',
    key: 'provider',
    render: (row) => h('div', [
      h('div', { style: 'font-weight: 500; margin-bottom: 4px' }, row.name),
      h('div', { 
        style: 'color: #666; font-size: 12px' 
      }, `Created: ${formatDate(row.created_at)}`)
    ])
  },
  {
    title: 'Status',
    key: 'status',
    render: (row) => h(NTag, { 
      type: getStatusType(row.status) 
    }, { 
      default: () => row.status 
    })
  },
  {
    title: 'Verification',
    key: 'verification',
    render: (row) => {
      if (!row.verification_info) {
        return h(NTag, { type: 'default' }, { default: () => 'No data' })
      }
      
      if (row.verification_info.digital_signature_confirmed && !row.verification_info.admin_viewed_at) {
        return h(NTag, { type: 'warning' }, { 
          default: () => 'Pending Review' 
        })
      }
      
      if (row.verification_info.admin_viewed_at) {
        return h(NTag, { type: 'info' }, { 
          default: () => 'Reviewed' 
        })
      }
      
      return h(NTag, { type: 'default' }, { default: () => 'Not submitted' })
    }
  },
  {
    title: 'Last Updated',
    key: 'updated_at',
    render: (row) => formatDate(row.updated_at)
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (row) => h('div', { style: 'display: flex; gap: 8px; flex-wrap: wrap' }, [
      // View button
      h(NButton, {
        size: 'small',
        onClick: () => goToProvider(row)
      }, { 
        icon: () => h(NIcon, null, { default: () => h(ExternalLink) }),
        default: () => 'View'
      }),
      
      // Review button (only for pending)
      ...(row.isPendingReview ? [
        h(NButton, {
          size: 'small',
          type: 'primary',
          onClick: () => viewProvider(row)
        }, { 
          icon: () => h(NIcon, null, { default: () => h(Eye) }),
          default: () => 'Review' 
        })
      ] : []),
      
      // Translate button
      h(NButton, {
        size: 'small',
        type: 'info',
        secondary: true,
        onClick: () => openTranslateModal(row)
      }, { 
        icon: () => h(NIcon, null, { default: () => h(Languages) }),
        default: () => 'Translate' 
      })
    ])
  }
]

// Methods
const loadAdminData = async () => {
  loading.value = true
  try {
    await loadProviders()
    calculateStats()
  } catch (error) {
    console.error('Error loading admin data:', error)
    message.error('Failed to load admin data')
  } finally {
    loading.value = false
  }
}

const loadProviders = async (page = 1) => {
  loadingProviders.value = true
  try {
    const params = {
      page: page,
      per_page: perPage.value,
      status_filter: filterStatus.value !== 'all' ? filterStatus.value : undefined,
      verification_pending: showPendingOnly.value ? true : undefined
    }

    const result = await getAdminProviders(authToken.value, params)
    
    if (result.success) {
      providers.value = result.providers.map(p => ({
        id: p.id,
        name: p.provider_name,
        status: p.status,
        verification_info: p.verification_info,
        created_at: p.created_at,
        updated_at: p.updated_at,
        hasClockClearUrl: p.verification_info?.clockclear_url ? true : false,
        isPendingReview: p.verification_info?.digital_signature_confirmed && !p.verification_info?.admin_viewed_at
      }))
      
      totalProviders.value = result.total
      currentPage.value = result.page
      totalPages.value = result.total_pages
      
      calculateStats()
    } else {
      message.error(result.error || 'Failed to load providers')
    }
  } catch (error) {
    console.error('Error loading providers:', error)
    message.error('Failed to load providers')
  } finally {
    loadingProviders.value = false
  }
}

const calculateStats = () => {
  stats.value.totalProviders = totalProviders.value
  stats.value.pendingVerifications = providers.value.filter(p => p.isPendingReview).length
  stats.value.verifiedProviders = providers.value.filter(p => p.status === 'verified').length
}

const handleFilterChange = () => {
  currentPage.value = 1
  loadProviders(1)
}

const handlePageChange = (page) => {
  loadProviders(page)
}

const goToProvider = (provider) => {
  const url = `/providers/${provider.id}`
  window.location.href = url
}

const viewProvider = (provider) => {
  selectedProvider.value = provider
  adminNotes.value = provider.verification_info?.verification_notes || ''
  showVerificationModal.value = true
}

const handleUpdateStatus = async (newStatus) => {
  if (!selectedProvider.value) return
  
  try {
    updatingStatus.value = true
    
    const result = await updateProviderStatus(authToken.value, {
      provider_id: selectedProvider.value.id,
      new_status: newStatus,
      admin_notes: adminNotes.value || undefined
    })
    
    if (result.success) {
      message.success(`Provider ${newStatus === 'verified' ? 'approved' : 'rejected'} successfully`)
      showVerificationModal.value = false
      selectedProvider.value = null
      adminNotes.value = ''
      await loadProviders(currentPage.value)
    } else {
      message.error(result.error || 'Failed to update provider status')
    }
  } catch (error) {
    console.error('Error updating provider status:', error)
    message.error('Failed to update provider status')
  } finally {
    updatingStatus.value = false
  }
}

// Translate methods
const openTranslateModal = (provider) => {
  translateProvider.value = provider
  translateConfirmText.value = ''
  showTranslateModal.value = true
}

const closeTranslateModal = () => {
  showTranslateModal.value = false
  translateProvider.value = null
  translateConfirmText.value = ''
}

const confirmTranslate = async () => {
  if (translateConfirmText.value !== 'TRANSLATE' || !translateProvider.value) return
  
  try {
    translating.value = true
    
    const result = await adminTranslateProvider(authToken.value, translateProvider.value.id)
    
    if (result.success) {
      message.success(`Provider "${translateProvider.value.name}" translated successfully`)
      closeTranslateModal()
      // Optionally reload providers to see updated data
      await loadProviders(currentPage.value)
    } else {
      message.error(result.error || 'Failed to translate provider')
    }
  } catch (error) {
    console.error('Error translating provider:', error)
    message.error('Failed to translate provider')
  } finally {
    translating.value = false
  }
}

const getStatusType = (status) => {
  switch (status) {
    case 'verified':
      return 'success'
    case 'unverified':
      return 'info'
    case 'rejected':
      return 'error'
    case 'suspended':
      return 'warning'
    default:
      return 'default'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleString()
}

// Lifecycle
onMounted(() => {
  if (!isAuthenticated.value) {
    console.log('Unauthorized access attempt')
    loading.value = false
    return
  }
  loadAdminData()
})
</script>

<style scoped>
.provider-dashboard-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  min-height: 400px;
}

.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.space-x-4 > * + * {
  margin-left: 1rem;
}

.space-x-3 > * + * {
  margin-left: 0.75rem;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-4 {
  margin-top: 1rem;
}

.ml-4 {
  margin-left: 1rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-xs {
  font-size: 0.75rem;
}

.text-gray-500 {
  color: #6b7280;
}

.text-gray-600 {
  color: #4b5563;
}

.block {
  display: block;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.justify-end {
  justify-content: flex-end;
}

.gap-2 {
  gap: 0.5rem;
}

.text-center {
  text-align: center;
}

/* Card styling to match provider profile */
:deep(.n-card) {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #eaeffa;
  margin-bottom: 20px;
}

:deep(.n-card-header) {
  background-color: #f5f8ff;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0e639c;
  padding: 16px 24px;
}

/* Responsive */
@media (max-width: 768px) {
  .provider-dashboard-container {
    padding: 10px;
  }
  
  .space-x-4 {
    flex-direction: column;
  }
  
  .space-x-4 > * + * {
    margin-left: 0;
    margin-top: 0.5rem;
  }
}
</style>